import { onScopeDispose, ref } from 'vue'

// Same thresholds as SearchSelectField, so both insurance searches behave alike.
export const MIN_QUERY_LENGTH = 2
export const SEARCH_DEBOUNCE_MS = 250

type Corporate = { id: number; key: string; title: string }
type SearchFn = (query: string, signal: AbortSignal) => Promise<Corporate[]>

export default function useCorporateSearch(searchFn: SearchFn) {
  const results = ref<Corporate[]>([])
  const loading = ref(false)
  const failed = ref(false)

  let timer: ReturnType<typeof setTimeout> | undefined
  let abortController: AbortController | null = null
  let currentRequest = 0

  // Bumping the request id makes a response that is still in flight stale.
  const cancelPending = () => {
    clearTimeout(timer)
    currentRequest += 1
    abortController?.abort()
    abortController = null
  }

  const search = (rawQuery: string) => {
    cancelPending()
    failed.value = false

    const query = rawQuery.trim()
    if (query.length < MIN_QUERY_LENGTH) {
      results.value = []
      loading.value = false
      return
    }

    const requestId = currentRequest
    loading.value = true
    timer = setTimeout(async () => {
      const controller = new AbortController()
      abortController = controller
      try {
        const items = await searchFn(query, controller.signal)
        if (requestId !== currentRequest) return
        results.value = items
      } catch {
        if (requestId !== currentRequest) return
        results.value = []
        failed.value = true
      } finally {
        if (requestId === currentRequest) {
          loading.value = false
          abortController = null
        }
      }
    }, SEARCH_DEBOUNCE_MS)
  }

  onScopeDispose(cancelPending)

  return { results, loading, failed, search }
}
